/**
 * S1 · DECK — phone home, the one screen.
 *
 * A pinned header that never scrolls (STATE word + ARMING line + exposure),
 * the BRACKET control, the BRACKET SUMMARY band, the filter row, then ONE
 * virtualized feed.
 *
 * The feed is virtualized at EVERY viewport through the single `VirtualList`
 * renderer: the CSS-hidden-but-mounted mobile branch is what froze the old
 * dashboard, so there is no second list component in this file or any other.
 *
 * THE TIME BRACKET. The feed and its aggregate are driven by one shared
 * range (lib/bracket.ts) — 24H · 7D · 30D · 90D · YTD · All · Custom. The
 * client resolves the preset to explicit instants and the server never sees
 * a preset name; `All` is the ABSENCE of both bounds, which costs nothing
 * because the scan is tenant-leading, keyset-paged and LIMITed. The reason
 * an unbounded window used to be forbidden was an unbounded FETCH, and that
 * failure mode is closed by the index, not by hiding the range.
 *
 * PROSE BUDGET. One line above the first datum: the arming sentence. The
 * `why` sentence, the formulas, the denominators and the projection caveats
 * are all still here — in titles and in the summary's `⋯` disclosure. A fact
 * may be demoted; it may never be deleted.
 *
 * Deliberately NOT on a feed row: both instruments' confidences, raw model
 * tokens, deal ids, size, per-call breakdowns, full text, price charts,
 * sparklines, equity. All are one tap away and none answers a ≤45 s
 * question. An equity number that moves for reasons outside his control
 * trains a dopamine check, not a defect check.
 *
 * The brand mark and the mode/venue readout live in the nav rail, not here:
 * a header that has to re-state the product's name on every surface is a
 * page, and this is a chassis.
 */

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { api, type CaseRow, type StatePayload, type SummaryPayload } from "../lib/api";
import { refetchMsFor, scopeParam, useBracket } from "../lib/bracket";
import { BracketBar } from "../lib/BracketBar";
import { navigate, setParams, useWideViewport } from "../lib/route";
import { SpanInline } from "../lib/span";
import { age, clock } from "../lib/time";
import { VirtualList } from "../lib/VirtualList";
import { RaceGlyph } from "./RaceGlyph";

/* The feed row is fixed-height BY CONTRACT — that is what keeps the
 * materialised window a function of the viewport alone. These two constants
 * are the same numbers as `--row-h` in tokens.css (112 below 880 px, 80 at or
 * above it). Change one and you must change the other, or the virtualizer's
 * scroll space stops matching what is drawn — which shows up as scroll drift
 * on a long feed, not as a static error. */
const ROW_PX_NARROW = 112;
const ROW_PX_WIDE = 80;
const ROW_BREAKPOINT = 880;
const PAGE = 50;

type Filter = "all" | "strikes" | "faults";

/** The source filter is SERVER-SIDE. Filtering a truncated page in the
 *  client would render a count over a population nobody asked for. */
const SOURCES = [
  { key: "all", label: "ALL" },
  { key: "truth_social", label: "TRUMP" },
  { key: "telegram_farsna", label: "FARSNA" },
  { key: "axios", label: "AXIOS" },
] as const;
type SourceKey = (typeof SOURCES)[number]["key"];

function sourceFromUrl(): SourceKey {
  if (typeof location === "undefined") return "all";
  const raw = new URLSearchParams(location.search).get("source");
  const hit = SOURCES.find((s) => s.key === raw);
  return hit ? hit.key : "all";
}

export function Deck() {
  const [filter, setFilter] = useState<Filter>("all");
  const [source, setSource] = useState<SourceKey>(sourceFromUrl);
  const { range } = useBracket();
  const wide = useWideViewport(ROW_BREAKPOINT);
  const rowPx = wide ? ROW_PX_WIDE : ROW_PX_NARROW;

  const scopeLabel = scopeParam(range);
  const sinceParam = range.since ?? undefined;
  const untilParam = range.until ?? undefined;
  const sourceParam = source === "all" ? undefined : source;
  const refetchMs = refetchMsFor(range);

  const pickSource = useCallback((s: SourceKey) => {
    setSource(s);
    setParams({ source: s === "all" ? null : s });
  }, []);

  const state = useQuery({
    queryKey: ["state"],
    queryFn: api.state,
    // Liveness: never served from cache, never 304. A cached liveness
    // reading is a lie, and a 304 here is how a dead bot renders as healthy.
    refetchInterval: 5000,
    staleTime: 0,
    gcTime: 0,
  });

  // The aggregate and the feed take the SAME bracket and the SAME source, so
  // the band above the list always describes the list below it.
  const summary = useQuery({
    queryKey: ["summary", source, range.since, range.until],
    queryFn: () =>
      api.summary({ since: sinceParam, until: untilParam, scope_label: scopeLabel, source: sourceParam }),
    refetchInterval: refetchMs,
  });

  const feed = useInfiniteQuery({
    queryKey: ["cases", filter, source, range.since, range.until],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.cases({
        limit: PAGE,
        cursor: pageParam,
        only: filter === "all" ? undefined : filter,
        since: sinceParam,
        until: untilParam,
        source: sourceParam,
        scope_label: scopeLabel,
      }),
    getNextPageParam: (last) => last.meta.next_cursor ?? undefined,
    refetchInterval: refetchMs,
  });

  const rows: CaseRow[] = useMemo(() => feed.data?.pages.flatMap((p) => p.rows) ?? [], [feed.data]);
  const skipped = useMemo(() => (feed.data?.pages ?? []).reduce((n, p) => n + (p.meta.skipped ?? 0), 0), [feed.data]);

  // A BUILD-LEVEL fact, not a row-level one: when the feed projection does
  // not carry the race summary it is absent from EVERY row, so it is stated
  // once — as a title — instead of consuming a line on all fifty of them.
  const raceMissing = useMemo(() => rows.length > 0 && rows.every((r) => r.race === undefined), [rows]);

  const onEnd = useCallback(() => {
    if (feed.hasNextPage && !feed.isFetchingNextPage) void feed.fetchNextPage();
  }, [feed]);

  const exposurePostId = state.data?.exposure?.post_id ?? null;
  const scope = feed.data?.pages[0]?.meta.scope ?? "";
  const sum = summary.data;

  return (
    <div className="deck">
      <StateHeader state={state.data} error={state.isError} />
      <BracketBar />
      <BracketSummary sum={sum} label={range.label} loading={summary.isLoading} error={summary.isError} />
      <div className="tabs">
        <div className="seg" role="tablist" aria-label="feed filter">
          {(["all", "strikes", "faults"] as Filter[]).map((f) => (
            <button
              key={f}
              className="seg__btn"
              role="tab"
              aria-selected={filter === f}
              onClick={() => setFilter(f)}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="seg" role="tablist" aria-label="source filter">
          {SOURCES.map((s) => (
            <button
              key={s.key}
              className="seg__btn"
              role="tab"
              aria-selected={source === s.key}
              onClick={() => pickSource(s.key)}
              title={s.key === "all" ? "every source" : s.key}
            >
              {s.label}
            </button>
          ))}
        </div>
        <span
          className="tabs__scope mono"
          title={raceMissing ? "race detail is not carried by the feed projection" : undefined}
        >
          {scope}
        </span>
      </div>

      <VirtualList
        rows={rows}
        ariaLabel="case feed"
        rowKey={(r) => r.post_id}
        estimateSize={rowPx}
        onEndReached={onEnd}
        emptyLabel={
          feed.isLoading ? (
            <div className="stub">loading…</div>
          ) : (
            <EmptyFeed
              label={range.label}
              facts={[scope, ...(state.data?.conditions ?? []).map((c) => `${c.name.toLowerCase()} ${c.state}`)]}
              filter={filter}
              onShowAll={() => setFilter("all")}
            />
          )
        }
        renderRow={(r) => <FeedRow row={r} exposurePostId={exposurePostId} />}
        footer={
          <div className="feed__footer muted">
            {feed.isFetchingNextPage && <span>loading…</span>}
            {skipped > 0 && (
              <span className="chip chip--down" title="rows the server could not project; never silently dropped">
                skipped {skipped}
              </span>
            )}
            {!feed.hasNextPage && rows.length > 0 && <span>end of range</span>}
          </div>
        }
      />
    </div>
  );
}

/* ------------------------------------------------------------------ header */

function StateHeader({ state, error }: { state: StatePayload | undefined; error: boolean }) {
  const exposure = state?.exposure ?? null;
  const word = error ? "UNKNOWN" : (state?.state ?? "…");
  return (
    /* data-exposure inverts the hierarchy in CSS with no JS measurement: with
       a position open the EXPOSURE line is the largest type on the screen and
       the state word steps down. A grey null word twice the size of a live
       levered position is the wrong ranking. */
    <header className="deck-header" data-exposure={exposure ? "1" : "0"}>
      {/* M6: 4 s, one eye, 3 a.m. One word. The evidence sentence behind the
          word rides its title — the header opens with ONE line of prose. */}
      <div className="state-word" data-state={word} aria-live="polite" title={state?.why ?? undefined}>
        {word}
      </div>
      <div className="arming">{error ? "⇒ the surface cannot reach its own API" : (state?.arming ?? "")}</div>

      {exposure && (
        /* THE ONLY AMBER ON THE SCREEN. A calm screen has zero amber and
           that is readable across a room (D4). */
        <div className="exposure">
          <div className="exposure__label">MONEY AT RISK</div>
          <div className="exposure__line mono">
            <span className="exposure__at-risk">
              {exposure.direction === "up" ? "LONG" : "SHORT"} {exposure.instrument} {exposure.size ?? "—"} @{" "}
              {exposure.entry_price ?? "—"}
            </span>
          </div>
          <div className="exposure__levels mono">
            SL {level(exposure.entry_price, exposure.sl_dist, exposure.direction, -1)} · TP{" "}
            {level(exposure.entry_price, exposure.tp_dist, exposure.direction, +1)} ·{" "}
            <HoldRemaining openTs={exposure.open_ts} holdSeconds={exposure.hold_seconds} />
          </div>
          <div className="exposure__id mono">
            {exposure.trade_id} · pairing {exposure.pairing} · state {exposure.state}
            {exposure.unterminated ? " · UNTERMINATED — asserted, unverified" : ""}
            {venueOf(exposure.mode, exposure.venue)}
          </div>
        </div>
      )}

      <ConditionStrip state={state} />
    </header>
  );
}

/** SL/TP are drawn from the entry price and the trade's OWN stored distance.
 *  Both come from the same server row; nothing is inferred from a partial
 *  page. Missing either renders `—`, never a guessed level. */
function level(entry: number | null, dist: number | null, dir: string | null, sign: number): string {
  if (entry === null || dist === null) return "—";
  const s = dir === "up" ? sign : -sign;
  return (entry + s * dist).toFixed(2);
}

/** The CONFIGURED hold is a setting; the REMAINING hold is the one number
 *  that says go look now. Counted from `open_ts + hold_seconds`, both already
 *  on the row — the tick is local and re-fetches nothing. Past zero the hold
 *  is overdue and marked by WEIGHT, not by colour: this block is already the
 *  only amber on the screen and amber means money at risk, nothing else. */
function HoldRemaining({ openTs, holdSeconds }: { openTs: string | null; holdSeconds: number | null }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (holdSeconds === null || holdSeconds === undefined) return <>hold —</>;
  const opened = openTs ? Date.parse(openTs) : NaN;
  if (!Number.isFinite(opened)) {
    return (
      <span title="configured hold; this order carries no open timestamp to count down from">
        hold {age(holdSeconds)}
      </span>
    );
  }
  const remaining = (opened + holdSeconds * 1000 - Date.now()) / 1000;
  const title = `configured hold ${age(holdSeconds)}`;
  return remaining >= 0 ? (
    <span title={title}>hold {age(remaining)} left</span>
  ) : (
    <strong className="exposure__overdue" title={`${title}; the close is past due`}>
      hold EXPIRED +{age(-remaining)}
    </strong>
  );
}

/** Mode and venue are properties of THIS position, not of the console, so
 *  they ride the exposure id line. Absent fields print nothing rather than a
 *  dangling separator. */
function venueOf(mode: string | null, venue: string | null): string {
  const parts = [mode, venue].filter((x): x is string => Boolean(x));
  return parts.length ? ` · ${parts.join(" ")}` : "";
}

function ConditionStrip({ state }: { state: StatePayload | undefined }) {
  if (!state) return null;
  const surface = state.surface;
  const lag = surface?.tailer_lag_ms;
  return (
    <div className="cond-strip">
      {state.conditions.map((c) => (
        <button
          key={c.name}
          className={`cond cond--${c.state.toLowerCase()}`}
          onClick={() => navigate({ name: "conditions" })}
          /* title is unreachable by touch and by keyboard; the state and what
             it blocks belong in the accessible name. */
          aria-label={`${c.name} ${c.state}${c.blocks ? `, blocks ${c.blocks}` : ""}`}
          title={c.blocks ? `blocks: ${c.blocks}` : c.state}
        >
          {/* The full name, not the first four characters: FIREHOSE truncated
              to "fire" collides with this product's own meaning of the word. */}
          <span className="cond__name">{c.name.toLowerCase()}</span>
          <span className="cond__state">{c.state}</span>
        </button>
      ))}
      {surface && (
        <span
          className="surface-lag mono"
          title="the observation surface publishes its own lag; when the observer is late you learn it before you mistrust the numbers"
        >
          surface lag {lag === null || lag === undefined ? "∅" : `${Math.round(lag)}ms`}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------- the bracket band */

/** What happened IN THIS BRACKET. The default reading is four numbers and
 *  their labels — the formula, the clock and the denominators live in the
 *  titles and in the `⋯` disclosure, one tap from the number they qualify.
 *
 *  Every figure is one SQL aggregate over the same predicate the feed below
 *  it uses, so the band and the list can never disagree. */
function BracketSummary({
  sum,
  label,
  loading,
  error,
}: {
  sum: SummaryPayload | undefined;
  label: string;
  loading: boolean;
  error: boolean;
}) {
  if (error) {
    return (
      <div className="summary">
        <span className="summary__label">{label}</span>
        <span className="mono dim">summary unavailable</span>
      </div>
    );
  }
  if (!sum) {
    return (
      <div className="summary">
        <span className="summary__label">{label}</span>
        <span className="mono dim">{loading ? "…" : "—"}</span>
      </div>
    );
  }
  const c = sum.cases;
  const d = c.denominators;
  const unplaceable = d?.excluded_out_of_scope ?? 0;
  return (
    <div className="summary">
      <span className="summary__label">{sum.window?.label ?? label}</span>
      <div className="summary__figs">
        <SumFig
          v={c.n}
          k="cases"
          title={`cases whose source timestamp falls in this range · clock: ${sum.clocks?.cases ?? "created_at"}`}
        />
        <SumFig v={c.strikes} k="strikes" title="cases that reached ORDERED, FILLED, CLOSED or SETTLED" />
        <SumFig
          v={c.faults}
          k="faults"
          title="cases whose machine verdict is FAULT or UNKNOWN; DEGRADED is not one, so this reconciles with the state read"
        />
        <SumFig v={c.no_trade} k="no-trade" title="cases that carry a no-trade reason" />
        {/* THE BALANCE, on the page you open first. The old dashboard kept it
            top-left on every page; the rebuild left it on LEDGER only — the
            last unclosed row of the benchmark gap list (GAP 2). Same figure,
            same attestation, same read-age the ledger shows: it comes off the
            SAME /summary payload this band already fetched, so it moves with
            the bracket and costs nothing. */}
        {sum.money?.balance && (
          <div
            className="fig"
            title={`latest daily balance snapshot · ${Math.round(sum.money.balance.read_age_s / 3600)}h old · clock: ${
              sum.clocks?.money ?? "close_ts"
            }`}
          >
            <span className="fig__v mono num">
              ${sum.money.balance.value.toFixed(2)}
              {sum.money.balance.read_age_s > 86400 ? " ⚠" : ""}
            </span>{" "}
            <span className="fig__k">balance</span>
          </div>
        )}
      </div>
      <details className="summary__more">
        <summary>⋯</summary>
        <div className="summary__detail">
          <dl className="kvs">
            {c.by_source?.length > 0 && (
              <>
                <dt className="kv__k">by source</dt>
                <dd className="kv__v mono">{c.by_source.map((r) => `${r.source} ${r.n}`).join(" · ")}</dd>
              </>
            )}
            {c.by_outcome?.length > 0 && (
              <>
                <dt className="kv__k">by outcome</dt>
                <dd className="kv__v mono">{c.by_outcome.map((r) => `${r.outcome_word} ${r.n}`).join(" · ")}</dd>
              </>
            )}
            <dt className="kv__k">range</dt>
            <dd className="kv__v mono">
              {sum.window?.is_all
                ? "no bounds — every case this tenant holds"
                : `${sum.window?.since ?? "open"} → ${sum.window?.until ?? "now"}`}
              {c.first_at ? ` · first ${c.first_at}` : ""}
              {c.last_at ? ` · last ${c.last_at}` : ""}
            </dd>
            <dt className="kv__k">counted</dt>
            <dd className="kv__v mono">
              {d?.included ?? c.n} cases{d?.lower_bound ? " · lower bound" : ""}
            </dd>
            <dt className="kv__k">not counted</dt>
            <dd className="kv__v">
              {unplaceable} cases carry no source timestamp — not placeable in this range
            </dd>
            {sum.clocks && (
              <>
                <dt className="kv__k">clocks</dt>
                <dd className="kv__v mono">
                  {Object.entries(sum.clocks)
                    .map(([k, v]) => `${k} ${v}`)
                    .join(" · ")}
                </dd>
              </>
            )}
          </dl>
        </div>
      </details>
    </div>
  );
}

function SumFig({ v, k, title }: { v: number | null | undefined; k: string; title: string }) {
  return (
    <span className="sumfig" title={title}>
      <span className="sumfig__v mono num">{v ?? "—"}</span> <span className="sumfig__k">{k}</span>
    </span>
  );
}

/* ------------------------------------------------------------- empty state */

/** The first-run screen a fresh node actually shows its owner. Built ONLY
 *  from data already in hand — the page's own scope and the conditions the
 *  state endpoint already returned. No invented state, no invented advice. */
function EmptyFeed({
  label,
  facts,
  filter,
  onShowAll,
}: {
  label: string;
  facts: string[];
  filter: Filter;
  onShowAll: () => void;
}) {
  const shown = facts.filter(Boolean);
  return (
    <div className="empty">
      <div
        className="empty__title"
        title="every arrival is recorded and scored, including the ones that never trade"
      >
        no cases in {label}
      </div>
      {shown.length > 0 && <div className="empty__facts mono">{shown.join(" · ")}</div>}
      {filter !== "all" && (
        <div className="empty__action">
          showing {filter.toUpperCase()} only ·{" "}
          <button className="linkish" onClick={onShowAll}>
            show all
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- feed row */

const AT_RISK_STATES = new Set(["ORDERED", "FILLED"]);

const NO_SIGNAL_TITLE = "the feed projection carries no signal field for this row";

export function FeedRow({ row, exposurePostId }: { row: CaseRow; exposurePostId: string | null }) {
  // Amber marks money at risk RIGHT NOW: a case whose order is still open,
  // or the case behind the live position. A SETTLED case is not at risk and
  // is deliberately NOT amber — permanently amber rows burn the signal.
  const atRisk = AT_RISK_STATES.has(row.state) || row.post_id === exposurePostId;
  const fault = row.machine_verdict === "FAULT" || row.machine_verdict === "UNKNOWN";
  const conf = row.conf === null || row.conf === undefined ? "" : ` · conf ${row.conf}`;
  // The fallback chain, best evidence first: the signal itself, then the
  // instrument and direction the order actually carried, then the refusal
  // reason, then nothing. A row that ORDERED never reads as "no signal" —
  // where the projection is thin the fact rides a title, not the line.
  const acted = AT_RISK_STATES.has(row.state) || row.state === "SETTLED" || row.state === "CLOSED";
  let meta: { text: string; dim: boolean; title?: string };
  if (row.signal) {
    meta = { text: `${row.signal}${conf}`, dim: false };
  } else if (row.instrument && row.direction) {
    meta = { text: `${row.instrument} ${row.direction}${conf}`, dim: false, title: acted ? NO_SIGNAL_TITLE : undefined };
  } else if (row.no_trade_reason) {
    meta = { text: row.no_trade_reason, dim: false };
  } else {
    meta = { text: "—", dim: true, title: acted ? NO_SIGNAL_TITLE : undefined };
  }
  return (
    <button
      className={`row${atRisk ? " row--ordered" : ""}${fault ? " row--fault" : ""}`}
      onClick={() => navigate({ name: "case", postId: row.post_id })}
      data-state={row.state}
      data-post-id={row.post_id}
    >
      <span className="row__when mono">{clock(row.created_at)}</span>
      <span className="row__source">{row.source}</span>
      <span className="row__outcome">{row.outcome_word}</span>

      <span className="row__tags">
        {fault && <span className="chip chip--down">{row.machine_verdict}</span>}
        {row.machine_verdict === "DEGRADED" && <span className="chip">DEGRADED</span>}
        {row.via === "mesh" && (
          <span className="chip" title="delivered by a peer, not detected locally">
            mesh
          </span>
        )}
        {row.settlement === "UNRESOLVED" && (
          <span
            className="chip chip--down"
            title="the evidence ladder bottomed out — a fault, not a state to average over"
          >
            UNRESOLVED
          </span>
        )}
        {row.revision > 1 && (
          <span className="chip" title="late enrichment landed">
            rev {row.revision}
          </span>
        )}
      </span>

      {/* dir="auto" or an RTL excerpt is laid out LTR and the ellipsis eats
          the START of the sentence rather than its end. */}
      <div className="row__excerpt" dir="auto" lang={row.source === "telegram_farsna" ? "fa" : undefined}>
        {row.excerpt || <span className="dim">— no text on this arrival</span>}
      </div>

      <span className="row__meta mono">
        <span className={meta.dim ? "dim" : undefined} title={meta.title}>
          {meta.text}
        </span>
        <SpanInline
          ms={row.headline_span_ms}
          clock={row.headline_clock}
          certainty={row.headline_certainty}
          hop={row.headline_span_hop}
        />
        <RaceGlyph race={row.race} source={row.source} />
      </span>

      {/* WIN / LOSS is a CATEGORY, not a number: no up/down colour. */}
      <span className="row__verdict">
        {row.strategy_verdict && row.strategy_verdict !== "NONE" ? row.strategy_verdict : ""}
      </span>
    </button>
  );
}
